<script lang="ts">
	import { pinchZoom } from '#lib/pinch-zoom.ts';
	import { globalState } from '#lib/util/global-state.svelte.ts';
	import type { AssetRef } from '#src/types/chat.ts';

	// Mirrors the dimensions the image model is asked for (see rag's
	// _DIMENSIONS_BY_FORMAT): portrait/landscape are the DIN A-series ratio.
	const RATIO_BY_IMAGE_FORMAT = {
		square: 1,
		portrait: 1024 / 1440,
		landscape: 1440 / 1024
	};

	interface Props {
		asset: AssetRef;
		/** Makes the frame a button, e.g. to open the image in a lightbox. */
		onclick?: () => void;
	}

	const { asset, onclick }: Props = $props();

	// Captured once on mount: only assets that started out generating (not ones
	// already 'ready' when the component first appeared, e.g. loaded from history)
	// should get the reveal animation.
	const wasGenerating = asset.status !== 'ready';
	let imageLoaded = $state(false);
	let revealDone = $state(!wasGenerating);
	// Taken from the loaded image itself: generated images aren't always square
	// (see the instance's image format setting).
	let aspectRatio = $state<number | null>(null);

	// The frame has no other height source — its children are positioned
	// absolutely and MessageAssets lays the row out align-items: flex-start — so
	// it must carry a ratio from the very first render or it collapses to zero
	// height until the image loads, which also leaves loading="lazy" observing a
	// zero-area box. Every asset therefore gets a guess, not just a generating
	// one: the instance's format setting is what a generated image will be, and
	// the closest thing to a default for an upload. Matches query_rag.py, which
	// only applies the format to antonio instances and leaves everyone else
	// square. `?? 1` covers a format value the instance record shouldn't hold.
	const placeholderRatio = $derived(
		RATIO_BY_IMAGE_FORMAT[
			globalState.instance?.feature_flags?.antonioChat?.imageFormat ?? 'square'
		] ?? 1
	);

	// The frame fills the width of the message row (flex: 1 1 0), so a tall image
	// would grow past the 70vh cap and get clamped — which squashes the box back
	// towards square no matter what ratio is set. Capping the width at the ratio's
	// worth of 70vh keeps the box on ratio instead of letting the height clamp win.
	const effectiveRatio = $derived(aspectRatio ?? placeholderRatio);
	const maxWidth = $derived(`min(100%, ${effectiveRatio * 70}vh)`);

	function handleLoad(event: Event) {
		const { naturalWidth, naturalHeight } = event.currentTarget as HTMLImageElement;
		if (naturalWidth && naturalHeight) {
			aspectRatio = naturalWidth / naturalHeight;
		}
		imageLoaded = true;
	}
</script>

{#snippet frameContent()}
	{#if wasGenerating && !revealDone}
		<div class="asset-skeleton"></div>
	{/if}
	{#if asset.status === 'ready' && asset.url}
		<img
			class="asset-image"
			class:asset-image-reveal={wasGenerating}
			class:is-loaded={imageLoaded}
			src={asset.url}
			alt={asset.prompt || ''}
			loading="lazy"
			onload={handleLoad}
			onanimationend={() => (revealDone = true)}
			{@attach pinchZoom}
		/>
	{/if}
{/snippet}

{#if onclick}
	<button
		type="button"
		class="asset-frame"
		title={asset.name}
		{onclick}
		style:aspect-ratio={effectiveRatio}
		style:max-width={maxWidth}
	>
		{@render frameContent()}
	</button>
{:else}
	<div class="asset-frame" style:aspect-ratio={effectiveRatio} style:max-width={maxWidth}>
		{@render frameContent()}
	</div>
{/if}

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.asset-frame {
		position: relative;
		flex: 1 1 0;
		min-width: 20%;
		max-width: 100%;
		max-height: 70vh;
		border-radius: 0.5rem;
		overflow: hidden;
		border: 1px solid var(--border-subtle);

		// Undo the base button styles: their fixed height would override the aspect ratio.
		&:is(button) {
			height: auto;
			padding: 0;
			background: transparent;
			cursor: zoom-in;

			&:hover {
				background: transparent;
			}
		}

		.asset-skeleton {
			position: absolute;
			inset: 0;
			background: var(--surface-2);

			@include lib.skeleton-pulse;
		}

		.asset-image {
			position: absolute;
			inset: 0;
			width: 100%;
			height: 100%;
			object-fit: contain;

			&.asset-image-reveal {
				opacity: 0;
				filter: blur(4rem);

				&.is-loaded {
					animation: asset-reveal 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
				}
			}
		}
	}

	@keyframes asset-reveal {
		from {
			opacity: 0;
			scale: 1.2;
			filter: blur(4rem);
		}
		to {
			opacity: 1;
			scale: 1;
			filter: blur(0);
		}
	}
</style>
